import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Building2, 
  Users, 
  TrendingUp, 
  Target,
  UserPlus,
  Mail,
  Download,
  BarChart3,
  ChevronRight,
  Loader2,
  LogOut,
  Send,
  CheckCircle2,
  Clock,
  AlertCircle,
  Plus,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { logger } from '../utils/logger';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INDUSTRY_NAMES = {
  healthcare: 'Healthcare',
  it_tech: 'IT/Technology',
  finance: 'Finance',
  education: 'Education',
  retail: 'Retail'
};

const COLORS = ['#2563EB', '#0284C7', '#06B6D4', '#7C3AED', '#D97706'];

const CORP_CHART_TOOLTIP = { borderRadius: '12px', border: '1px solid #E7E5E4' };
const CORP_CHART_DOT = { fill: '#2563EB' };

const CorporateDashboard = () => {
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [organization, setOrganization] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [industryDialogOpen, setIndustryDialogOpen] = useState(false);
  const [customIndustry, setCustomIndustry] = useState({ name: '', description: '', questions: [{ text: '', options: ['', '', '', ''] }] });
  const [creatingIndustry, setCreatingIndustry] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      
      const [orgRes, analyticsRes, employeesRes, invitesRes] = await Promise.all([
        fetch(`${API}/corporate/organization`, { headers, credentials: 'include' }),
        fetch(`${API}/corporate/analytics`, { headers, credentials: 'include' }),
        fetch(`${API}/corporate/employees`, { headers, credentials: 'include' }),
        fetch(`${API}/corporate/pending-invites`, { headers, credentials: 'include' })
      ]);

      if (orgRes.ok) setOrganization(await orgRes.json());
      if (analyticsRes.ok) setAnalytics(await analyticsRes.json());
      if (employeesRes.ok) {
        const data = await employeesRes.json();
        setEmployees(data.employees);
      }
      if (invitesRes.ok) {
        const data = await invitesRes.json();
        setPendingInvites(data.invites);
      }
    } catch (error) {
      logger.error('Failed to fetch data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const handleExport = async (format) => {
    try {
      const response = await fetch(`${API}/corporate/export/${format}`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        
        if (format === 'csv') {
          const blob = new Blob([data.csv], { type: 'text/csv' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `ei_assessment_export_${new Date().toISOString().split('T')[0]}.csv`;
          a.click();
        } else {
          const blob = new Blob([JSON.stringify(data.data, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `ei_assessment_export_${new Date().toISOString().split('T')[0]}.json`;
          a.click();
        }
        
        toast.success(`Data exported as ${format.toUpperCase()}`);
      }
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map((n) => n[0]).join('').toUpperCase() || 'U';
  };

  const addQuestion = () => {
    setCustomIndustry(prev => ({
      ...prev,
      questions: [...prev.questions, { text: '', options: ['', '', '', ''] }]
    }));
  };

  const removeQuestion = (idx) => {
    setCustomIndustry(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== idx)
    }));
  };

  const updateQuestion = (idx, field, value) => {
    setCustomIndustry(prev => {
      const qs = [...prev.questions];
      if (field === 'text') { qs[idx] = { ...qs[idx], text: value }; }
      return { ...prev, questions: qs };
    });
  };

  const updateOption = (qIdx, oIdx, value) => {
    setCustomIndustry(prev => {
      const qs = [...prev.questions];
      const opts = [...qs[qIdx].options];
      opts[oIdx] = value;
      qs[qIdx] = { ...qs[qIdx], options: opts };
      return { ...prev, questions: qs };
    });
  };

  const handleCreateIndustry = async () => {
    if (!customIndustry.name.trim()) { toast.error('Industry name is required'); return; }
    const validQs = customIndustry.questions.filter(q => q.text.trim() && q.options.filter(o => o.trim()).length >= 2);
    if (validQs.length === 0) { toast.error('Add at least one question with 2+ options'); return; }

    setCreatingIndustry(true);
    try {
      const payload = {
        name: customIndustry.name.trim(),
        description: customIndustry.description.trim(),
        questions: validQs.map(q => ({
          question_text: q.text.trim(),
          options: q.options.filter(o => o.trim()).map(o => ({ text: o.trim(), score: 1 }))
        }))
      };
      const response = await fetch(`${API}/corporate/custom-industry`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      if (response.ok) {
        toast.success('Custom industry created!');
        setIndustryDialogOpen(false);
        setCustomIndustry({ name: '', description: '', questions: [{ text: '', options: ['', '', '', ''] }] });
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to create industry');
      }
    } catch (error) {
      toast.error('Failed to create industry');
    } finally {
      setCreatingIndustry(false);
    }
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'Excellent': return 'text-white bg-black';
      case 'Very Good': return 'text-white bg-black';
      case 'Good': return 'text-amber-600 bg-amber-100';
      default: return 'text-smoke bg-white/5';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    );
  }

  const levelData = analytics?.summary?.level_distribution 
    ? Object.entries(analytics.summary.level_distribution).map(([name, value]) => ({ name, value }))
    : [];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-slate-950 text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2.5" data-testid="corporate-logo">
              <div className="w-9 h-9 rounded-lg bg-plum-voltage flex items-center justify-center">
                <Building2 className="h-5 w-5 text-white" strokeWidth={1.5} />
              </div>
              <div>
                <span className="font-semibold text-white">{organization?.name}</span>
                <span className="text-xs text-smoke block">
                  {INDUSTRY_NAMES[organization?.industry] || organization?.industry}
                </span>
              </div>
            </Link>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="rounded-full p-1" data-testid="user-menu-btn">
                <Avatar className="h-9 w-9">
                  <AvatarFallback className="bg-plum-voltage text-white">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 rounded-xl">
              <div className="px-3 py-2">
                <p className="font-medium">{user?.name}</p>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
                <p className="text-xs text-white mt-1">Corporate Admin</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive cursor-pointer">
                <LogOut className="h-4 w-4 mr-2" />
                Log Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8" data-testid="corporate-dashboard">
        {/* Welcome & Quick Actions */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Corporate Dashboard</h1>
            <p className="text-muted-foreground">
              Manage your team's emotional intelligence assessments
            </p>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className="rounded-full"
              onClick={() => setIndustryDialogOpen(true)}
              data-testid="add-custom-industry-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Custom Industry
            </Button>
            <Button 
              variant="outline" 
              className="rounded-full"
              onClick={() => setInviteDialogOpen(true)}
              data-testid="invite-employees-btn"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Invite Employees
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="rounded-full" data-testid="export-btn">
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => handleExport('csv')}>
                  Export as CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('json')}>
                  Export as JSON
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="rounded-2xl border-white/10" data-testid="stat-employees">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-black flex items-center justify-center">
                  <Users className="h-6 w-6 text-white" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{analytics?.summary?.total_employees || 0}</p>
                  <p className="text-sm text-muted-foreground">Total Employees</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-white/10" data-testid="stat-assessments">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center">
                  <Target className="h-6 w-6 text-amber-600" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{analytics?.summary?.total_assessments || 0}</p>
                  <p className="text-sm text-muted-foreground">Assessments Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-white/10" data-testid="stat-avg-score">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-white/5 flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-white" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{analytics?.summary?.average_score?.toFixed(1) || 0}%</p>
                  <p className="text-sm text-muted-foreground">Average Score</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-white/10" data-testid="stat-pending">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-blue-100 flex items-center justify-center">
                  <Mail className="h-6 w-6 text-white" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{pendingInvites.length}</p>
                  <p className="text-sm text-muted-foreground">Pending Invites</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          {/* Progress Over Time */}
          <Card className="rounded-2xl border-white/10 lg:col-span-2" data-testid="progress-chart">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-white" />
                Team Progress Over Time
              </CardTitle>
            </CardHeader>
            <CardContent>
              {analytics?.progress_over_time?.length > 0 ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={analytics.progress_over_time.slice(-20)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                      <XAxis dataKey="date" stroke="#78716C" fontSize={10} tickFormatter={(v) => new Date(v).toLocaleDateString()} />
                      <YAxis domain={[0, 100]} stroke="#78716C" fontSize={12} />
                      <Tooltip 
                        contentStyle={CORP_CHART_TOOLTIP}
                        labelFormatter={(v) => new Date(v).toLocaleDateString()}
                      />
                      <Line type="monotone" dataKey="score" stroke="#2563EB" strokeWidth={2} dot={CORP_CHART_DOT} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  No assessment data yet
                </div>
              )}
            </CardContent>
          </Card>

          {/* Level Distribution */}
          <Card className="rounded-2xl border-white/10" data-testid="level-distribution">
            <CardHeader>
              <CardTitle>Level Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              {levelData.some(d => d.value > 0) ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={levelData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {levelData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  No data available
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Department Analytics */}
        {analytics?.department_analytics?.length > 0 && (
          <Card className="rounded-2xl border-white/10 mb-8" data-testid="department-analytics">
            <CardHeader>
              <CardTitle>Department Performance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analytics.department_analytics}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                    <XAxis dataKey="department" stroke="#78716C" fontSize={12} />
                    <YAxis domain={[0, 100]} stroke="#78716C" fontSize={12} />
                    <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E7E5E4' }} />
                    <Bar dataKey="avg_score" fill="#2563EB" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Employees List */}
        <Card className="rounded-2xl border-white/10" data-testid="employees-list">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-white" />
              Team Members
            </CardTitle>
          </CardHeader>
          <CardContent>
            {employees.length > 0 ? (
              <div className="space-y-3">
                {employees.map((emp) => (
                  <div 
                    key={emp.user_id}
                    className="flex items-center justify-between p-4 rounded-2xl bg-black hover:bg-muted transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <Avatar>
                        <AvatarFallback className="bg-black text-white">
                          {getInitials(emp.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">{emp.name}</p>
                        <p className="text-sm text-muted-foreground">{emp.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">{emp.department || 'General'}</p>
                        <p className="text-xs text-muted-foreground">
                          {emp.assessments_completed} assessments
                        </p>
                      </div>
                      {emp.latest_score !== null && (
                        <div className="w-12 h-12 rounded-xl bg-black flex items-center justify-center">
                          <span className="font-bold text-white">{emp.latest_score?.toFixed(0)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Users className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">No employees yet</p>
                <Button 
                  className="rounded-full"
                  onClick={() => setInviteDialogOpen(true)}
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Invite Employees
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Category Averages */}
        {analytics?.category_averages && Object.keys(analytics.category_averages).length > 0 && (
          <Card className="rounded-2xl border-white/10 mt-8" data-testid="category-averages">
            <CardHeader>
              <CardTitle>EI Competency Averages</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(analytics.category_averages).map(([category, score]) => (
                  <div key={category} className="p-4 rounded-2xl bg-black">
                    <p className="text-sm text-muted-foreground capitalize mb-2">
                      {category.replace('_', ' ')}
                    </p>
                    <p className="text-2xl font-bold text-foreground">{score}%</p>
                    <Progress value={score} className="h-2 mt-2" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Invite Dialog */}
      <InviteDialog 
        open={inviteDialogOpen} 
        onOpenChange={setInviteDialogOpen}
        onSuccess={fetchData}
      />

      <Dialog open={industryDialogOpen} onOpenChange={setIndustryDialogOpen}>
        <DialogContent className="max-w-2xl rounded-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-white" />
              Create Custom Industry
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 mt-2">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Industry Name</Label>
              <Input
                placeholder="e.g. Manufacturing, Legal, Non-profit..."
                className="rounded-xl h-11"
                value={customIndustry.name}
                onChange={(e) => setCustomIndustry(prev => ({ ...prev, name: e.target.value }))}
                data-testid="industry-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Description (optional)</Label>
              <Textarea
                placeholder="Brief description of this industry assessment..."
                className="rounded-xl resize-none"
                rows={2}
                value={customIndustry.description}
                onChange={(e) => setCustomIndustry(prev => ({ ...prev, description: e.target.value }))}
                data-testid="industry-description-input"
              />
            </div>
            <div className="border-t border-border/40 pt-4">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-sm font-medium">Assessment Questions</Label>
                <Button variant="ghost" size="sm" className="rounded-full h-8 text-xs" onClick={addQuestion} data-testid="add-question-btn">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add Question
                </Button>
              </div>
              <div className="space-y-4">
                {customIndustry.questions.map((q, qIdx) => (
                  <Card key={`question-${qIdx}-${q.text.slice(0,10)}`} className="rounded-2xl border-black/[0.04]">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 space-y-1">
                          <Label className="text-xs text-muted-foreground">Question {qIdx + 1}</Label>
                          <Input
                            placeholder="Enter your question..."
                            className="rounded-lg h-10 text-sm"
                            value={q.text}
                            onChange={(e) => updateQuestion(qIdx, 'text', e.target.value)}
                            data-testid={`question-${qIdx}-text`}
                          />
                        </div>
                        {customIndustry.questions.length > 1 && (
                          <Button variant="ghost" size="sm" className="rounded-full h-8 w-8 p-0 mt-5 text-destructive" onClick={() => removeQuestion(qIdx)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {q.options.map((opt, oIdx) => (
                          <Input
                            key={`opt-${qIdx}-${oIdx}`}
                            placeholder={`Option ${String.fromCharCode(65 + oIdx)}`}
                            className="rounded-lg h-9 text-sm"
                            value={opt}
                            onChange={(e) => updateOption(qIdx, oIdx, e.target.value)}
                            data-testid={`question-${qIdx}-option-${oIdx}`}
                          />
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
            <Button
              onClick={handleCreateIndustry}
              disabled={creatingIndustry}
              className="w-full rounded-full h-11"
              data-testid="create-industry-submit-btn"
            >
              {creatingIndustry ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Create Industry
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const InviteDialog = ({ open, onOpenChange, onSuccess }) => {
  const { getAuthHeaders } = useAuth();
  const [emails, setEmails] = useState('');
  const [department, setDepartment] = useState('');
  const [sending, setSending] = useState(false);

  const handleInvite = async () => {
    const emailList = emails.split(/[,\n]/).map(e => e.trim()).filter(e => e);
    
    if (emailList.length === 0) {
      toast.error('Please enter at least one email');
      return;
    }

    setSending(true);
    try {
      const response = await fetch(`${API}/corporate/invite`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          emails: emailList,
          department: department || null
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Invited ${data.invited.length} employees`);
        if (data.already_exists.length > 0) {
          toast.info(`${data.already_exists.length} emails already registered`);
        }
        setEmails('');
        setDepartment('');
        onOpenChange(false);
        onSuccess();
      } else {
        toast.error('Failed to send invites');
      }
    } catch (error) {
      toast.error('Failed to send invites');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-3xl" data-testid="invite-dialog">
        <DialogHeader>
          <DialogTitle>Invite Employees</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label>Email Addresses</Label>
            <Textarea
              placeholder="Enter emails (one per line or comma-separated)"
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              rows={4}
              className="rounded-xl"
              data-testid="invite-emails-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Department (Optional)</Label>
            <Input
              placeholder="e.g., Engineering, Sales, HR"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              className="rounded-xl"
              data-testid="invite-department-input"
            />
          </div>
          <Button 
            onClick={handleInvite}
            disabled={sending}
            className="w-full rounded-full"
            data-testid="send-invites-btn"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
            Send Invitations
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
export default CorporateDashboard;
